import React, { Fragment, useState, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import classNames from "classnames";
import { usePopper } from "react-popper";
import { twClassNames } from "../../helpers/twClassNames";

interface Theme {
    activeItemClasses: string;
    inactiveItemClasses: string;
    disabledItemClasses: string;
    selectedItemClasses: string;
    menuButtonClasses: string;
    menuButtonIconClasses: string;
    menuButtonTextClasses: string;
    menuItemContainerClasses: string;
}
export function getTheme(
    lightMode,
    fullWidth = false,
    limitMenuWidth = false,
    iconOnly = false,
    menuButtonSize = "medium",
    icon = "",
    maxHeight?: string
): Theme {
    const darkMode = !lightMode;

    const itemStateClasses = "menu-item";
    const menuBtnBaseClasses = "btn2 px-3 group font-normal";
    const menuItemBaseClasses = "top-full mt-1 right-0";

    let baseIconClasses = "far transition duration-200 " + (icon || "fa-chevron-down");
    if (!iconOnly) {
        baseIconClasses += " text-xs";
    }

    return {
        activeItemClasses: classNames(itemStateClasses, {
            "active": lightMode,
            "active dark": darkMode
        }),
        inactiveItemClasses: classNames(itemStateClasses, {
            "inactive": lightMode,
            "inactive dark": darkMode
        }),
        disabledItemClasses: classNames(itemStateClasses, "cursor-not-allowed", {
            "disabled": lightMode,
            "disabled dark": darkMode
        }),
        selectedItemClasses: classNames(itemStateClasses, {
            "selected": lightMode,
            "selected dark": darkMode
        }),
        menuButtonClasses: classNames(
            menuBtnBaseClasses,
            menuButtonSize,
            {
                "secondary text-secondary group-hover:!text-gray-600": lightMode,
                "dark-secondary !text-gray-400 group-hover:!text-gray-300": darkMode
            },
            fullWidth && "w-full"
        ),
        menuButtonTextClasses: classNames("truncate font-medium", {
            "text-gray-700": lightMode,
            "text-gray-200": darkMode
        }),
        menuButtonIconClasses: baseIconClasses,
        menuItemContainerClasses: classNames(
            menuItemBaseClasses,
            { "menu-container": lightMode, "menu-container dark": darkMode },
            limitMenuWidth ? "max-w-full break-all !w-full" : "min-w-full break-all",
            maxHeight && maxHeight,
            maxHeight && "overflow-y-auto"
        )
    };
}

export interface DropdownItem {
    text: string | React.ReactNode;
    value: string | number | boolean;
    disabled?: boolean;
    className?: string;
    menuItemParentClassName?: string;
    icon?: string;
}

export interface DropdownProps {
    theme?: "light" | "dark";
    title?: string;
    titlePrefix?: React.ReactNode;
    titlePrefixClassName?: string;
    onItemSelected: (v: any, e: any) => void;
    items: DropdownItem[];
    icon?: string;
    disabled?: boolean;
    iconOnly?: boolean;
    fullWidth?: boolean;
    limitMenuWidth?: boolean;
    className?: string;
    autoSetTitle?: boolean;
    dropdownMenuStyling?: string;
    menuButtonSize?: "xxsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge";
    defaultSelectedItem?: DropdownItem;
    onItemMouseEnter?: (item: any) => void;
    onItemMouseLeave?: (item: any) => void;
    error?: boolean;
    itemComponent?: React.FC;
    label?: string;
    /** Optional class name for the menu button so callers can override styling */
    buttonClassName?: string;
    /** Optional max height for the dropdown menu with scrolling. Pass Tailwind classes like "max-h-80" (320px), "max-h-96" (384px), or "max-h-[500px]". When provided, enables overflow scrolling. */
    maxHeight?: string;
}

export function DefaultDropdownItem(item: DropdownItem) {
    return (
        <span
            className={twClassNames(
                "flex items-baseline gap-2 truncate",
                item.disabled ? "cursor-not-allowed !text-gray-300" : "",
                item.menuItemParentClassName ? item.menuItemParentClassName : ""
            )}
        >
            {item.icon && <i className={item.icon}></i>}
            {item.text || item.value}
        </span>
    );
}

export function Dropdown(props: DropdownProps) {
    const [title, setTitle] = useState(props.defaultSelectedItem?.text || props.title);
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);

    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: "bottom-start",
        modifiers: [
            {
                name: "flip",
                options: { fallbackPlacements: ["top-start", "bottom-end", "top-end"] }
            },
            { name: "preventOverflow", options: { boundary: document.body, padding: 8 } }
        ]
    });

    const theme = getTheme(
        props.theme ? props.theme === "light" : true,
        props.fullWidth,
        props.limitMenuWidth,
        props.iconOnly,
        props.menuButtonSize || "medium",
        props.icon,
        props.maxHeight
    );

    const onClickItem = (item: any, e = null) => {
        props.onItemSelected?.(item, e);
        if (props.autoSetTitle) {
            setTitle(item.text);
        }
    };

    const DropdownItemComponent = props.itemComponent || DefaultDropdownItem;

    useEffect(() => {
        if (props.defaultSelectedItem) {
            setTitle(props.defaultSelectedItem.text);
        }
    }, [props.defaultSelectedItem]);

    const customButtonClasses = props.buttonClassName
        ? classNames(
              "btn2 px-3 group font-normal",
              props.menuButtonSize || "medium",
              props.fullWidth && "w-full",
              props.buttonClassName
          )
        : null;

    return (
        <Menu
            as="div"
            className={"headless-menu group flex flex-col gap-1" + " " + (props.className || "")}
        >
            {props.label && (
                <div className="inputLabel text-sm font-medium text-gray-700">{props.label}</div>
            )}
            {props.iconOnly ? (
                <Menu.Button
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    ref={setReferenceElement}
                >
                    <i className={theme.menuButtonIconClasses}></i>
                </Menu.Button>
            ) : (
                <Menu.Button
                    as="div"
                    className={classNames(
                        customButtonClasses || theme.menuButtonClasses,
                        props.error ? "!border-pink-500 !text-pink-700" : "",
                        props.disabled ? "cursor-not-allowed" : "",
                        "justify-between"
                    )}
                    disabled={props.disabled}
                    ref={setReferenceElement}
                >
                    {props.titlePrefix && (
                        <span className={props.titlePrefixClassName || ""}>
                            {props.titlePrefix}
                        </span>
                    )}
                    {props.title && (
                        <span
                            className={classNames(
                                props.buttonClassName ? null : theme.menuButtonTextClasses,
                                props.error ? "!border-pink-500 !text-pink-700" : "",
                                props.disabled ? "cursor-not-allowed" : ""
                            )}
                        >
                            <DropdownItemComponent
                                {...{
                                    value: props.autoSetTitle
                                        ? (title as string)
                                        : (props.title as string),
                                    text: props.title || "",
                                    icon: props.icon,
                                    disabled: props.disabled
                                }}
                            />
                        </span>
                    )}
                    <i className={theme.menuButtonIconClasses}></i>
                </Menu.Button>
            )}

            <Transition
                as={Fragment}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
            >
                <Menu.Items
                    className={classNames(
                        theme.menuItemContainerClasses,
                        props.dropdownMenuStyling || null
                    )}
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                >
                    {props.items.map((item, i) => (
                        <Menu.Item key={i} disabled={item.disabled || false}>
                            {({ active }) => (
                                <div
                                    className={classNames(
                                        {
                                            [theme.activeItemClasses]: active,
                                            [theme.inactiveItemClasses]: !active,
                                            [theme.disabledItemClasses]: item.disabled
                                        },
                                        item.className
                                    )}
                                    onClick={(e: any) => onClickItem(item, e)}
                                    onMouseEnter={() => {
                                        props.onItemMouseEnter?.(item.value);
                                    }}
                                    onMouseLeave={() => {
                                        props.onItemMouseLeave?.(item.value);
                                    }}
                                >
                                    <DropdownItemComponent
                                        value={item.value}
                                        text={item.text}
                                        icon={item.icon}
                                        disabled={item.disabled}
                                        menuItemParentClassName={item.menuItemParentClassName}
                                    />
                                </div>
                            )}
                        </Menu.Item>
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
    );
}
